
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchUsersWithRoles, addUserRole, removeUserRole } from '@/services/roleManagementService';
import { UserWithRoles } from '@/types/supabase';
import { toast } from 'sonner';
import { Search, Plus, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNavigate } from 'react-router-dom';

const MentorsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  
  const { data: users, isLoading: isLoadingUsers, refetch: refetchUsers } = useQuery({
    queryKey: ['users-with-roles'],
    queryFn: fetchUsersWithRoles
  });
  
  const filteredMentors = users?.filter(user => {
    const hasName = 
      user.profile?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.profile?.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const hasEmail = user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const isMentor = user.roles.includes('mentor');
    
    return (hasName || hasEmail) && isMentor;
  });
  
  const filteredNonMentors = users?.filter(user => {
    const hasName = 
      user.profile?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.profile?.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const hasEmail = user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const isNotMentor = !user.roles.includes('mentor');
    
    return (hasName || hasEmail) && isNotMentor;
  });
  
  const handlePromoteToMentor = async (userId: string) => {
    try {
      await addUserRole(userId, 'mentor');
      refetchUsers();
    } catch (error) {
      console.error('Error promoting user to mentor:', error);
      toast.error('Failed to promote user to mentor');
    }
  };
  
  const handleRemoveMentorRole = async (userId: string) => {
    try {
      await removeUserRole(userId, 'mentor');
      refetchUsers();
    } catch (error) {
      console.error('Error removing mentor role:', error);
      toast.error('Failed to remove mentor role');
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search users..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate('/admin/users/create')}
          className="whitespace-nowrap"
        >
          Create User
        </Button>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="card-title">Current Mentors</CardTitle>
          <CardDescription>
            Users with mentor privileges
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingUsers ? (
            <div className="text-center py-4">Loading mentors...</div>
          ) : filteredMentors && filteredMentors.length > 0 ? (
            filteredMentors.map(user => <UserCard key={user.id} user={user} isMentor={true} onRoleChange={handleRemoveMentorRole} />)
          ) : (
            <div className="text-center py-4 text-muted-foreground">No mentors found</div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="card-title">Other Users</CardTitle>
          <CardDescription>
            Users without mentor privileges
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingUsers ? (
            <div className="text-center py-4">Loading users...</div>
          ) : filteredNonMentors && filteredNonMentors.length > 0 ? (
            filteredNonMentors.map(user => <UserCard key={user.id} user={user} isMentor={false} onRoleChange={handlePromoteToMentor} />)
          ) : (
            <div className="text-center py-4 text-muted-foreground">No users found</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

type UserCardProps = {
  user: UserWithRoles;
  isMentor: boolean;
  onRoleChange: (userId: string) => void;
};

const UserCard = ({ user, isMentor, onRoleChange }: UserCardProps) => {
  const fullName = `${user.profile?.first_name || ''} ${user.profile?.last_name || ''}`.trim();
  const initials = fullName ? `${user.profile?.first_name?.[0] || ''}${user.profile?.last_name?.[0] || ''}`.toUpperCase() : user.email.substring(0, 2).toUpperCase();
  
  return (
    <Card key={user.id} className="mb-4">
      <CardHeader className="flex flex-row items-center gap-4 pb-2">
        <Avatar className="h-12 w-12">
          <AvatarImage src={user.profile?.profile_photo_url || ''} alt={fullName || user.email} />
          <AvatarFallback className="bg-brand-indigo text-white">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <CardTitle className="text-lg card-title">{fullName || 'Unnamed User'}</CardTitle>
          <CardDescription>{user.email}</CardDescription>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {user.roles.map(role => (
            <Badge key={role} variant={role === 'admin' ? 'destructive' : role === 'mentor' ? 'default' : 'outline'}>
              {role}
            </Badge>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex justify-end">
          {isMentor ? (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-2"
              onClick={() => onRoleChange(user.id)}
            >
              <X size={16} /> Remove Mentor Role
            </Button>
          ) : (
            <Button 
              variant="default" 
              size="sm" 
              className="flex items-center gap-2"
              onClick={() => onRoleChange(user.id)}
            >
              <Plus size={16} /> Make Mentor
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MentorsList;
